/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/


import React, { useState, useMemo, useEffect } from 'react';
import HeaderSection from '../../../common/headerSection';
import PageHeader from '../../../common/pageHeader';
import { useTranslation } from 'react-i18next';
import { BrushCleaning, Bubbles, LogOut, Microscope, Search, ShelvingUnit, CircleX } from 'lucide-react';
import { MdCleaningServices, MdInventory, MdSanitizer } from 'react-icons/md';
import { postAutoclaveServices } from '../../../../service/postAutoclaveServices';
import Pagination from '../../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../../constants/constants';
import { MediaPowderProductMasterService } from '../../../../service/mediaPowderProductMaster';
import { helperFunctions } from '../../../../utils/helper';

interface StageDetail {
	status: 'COMPLETED' | 'PENDING' | 'IN PROGRESS';
	completed_at?: string;
	operator_name?: string;
	eligible?: string;
	rejected?: string;
	rejectionReason?: string;
}

interface BatchStats {
	totalBatches: number;
	totalPlates: number;
	pendingCleaning: number;
	pendingInspection: number;
	pendingSterilization: number;
	pendingInventory: number;
	completedBatches: number;
	totalRejected: number;
}

interface BatchStatus {
	id: string;
	mediaName: string;
	totalPlates: number;
	stages: {
		removal: StageDetail;
		cleaning: StageDetail;
		inspection: StageDetail;
		sterilization: StageDetail;
		inventory: StageDetail;
	};
	rejectedCount?: string;
	lastUpdated: string;
}

const PostAutoclaveProcess: React.FC = () => {

	const { t } = useTranslation();
	const [searchQuery, setSearchQuery] = useState('');
	const [mediaFilter, setMediaFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
	const [postAutoClaveBatches, setPostAutoClaveBatches] = useState<BatchStatus[]>();
	const [postAutoClaveBatchesStat, setPostAutoClaveBatchesStat] = useState<BatchStats>();
	// pagination
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [totalPages, setTotalPages] = useState(0);
	const [totalElements, setTotalElements] = useState(0);
	const [useEffectRequired, setUseEffectRequired] = useState(false);
	const [mediaTypes, setMediaTypes] = useState<any>();
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');


	const getStatusColor = (status: string) => {
		switch (status) {
			case 'COMPLETED': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
			case 'PENDING': return 'bg-amber-50 text-amber-600 border-amber-100';
			case 'IN PROGRESS': return 'bg-blue-50 text-blue-600 border-blue-100';
			case 'REJECTED': return 'bg-red-50 text-red-600 border-red-100';
			default: return 'bg-slate-50 text-slate-600 border-slate-100';
		}
	};

	//handle table pagination 
	const handlePageChange = (newPage: any, pageSize?: number) => {
		setPage(newPage);
		if (pageSize) {
			setPageSize(pageSize)
		}
		setUseEffectRequired(true)
	};

	//get all post autoclave batch stats
	const getPostAutoClaveBatchStat = async () => {
		try {
			const response = await postAutoclaveServices.getPostAutoClaveBatchStat();
			if (response.status && response.statusCode === 200) {

				setPostAutoClaveBatchesStat({
					totalBatches: Number(response.data.total_batches),
					totalPlates: Number(response.data.total_plates),
					pendingCleaning: Number(response.data.pending_cleaning),
					pendingInspection: Number(response.data.pending_inspection),
					pendingSterilization: Number(response.data.pending_sterilization),
					pendingInventory: Number(response.data.pending_sterilization),
					completedBatches: Number(response.data.total_completed),
					totalRejected: Number(response.data.total_rejected),
				});

			}
		} catch (error) {
			console.error('user response', error);
		}
	}

	//api call to get the plate types 
	const getPlateTypeList = async () => {
		try {
			const response = await MediaPowderProductMasterService.getMediaMasterList();
			if (response.status && response.statusCode === 200) {
				setMediaTypes(response.data)
			}
		} catch (error) {
			console.error('user response', error);
		}
	}

	//get all post autoclave batch list
	const getPostAutoclaveBatchList = async (page: number, pageSize: number, mediaType: string, searchQuery: string) => {
		try {
			const response = await postAutoclaveServices.getPostAutoclaveBatchList(page, pageSize, "id", "des", mediaType, searchQuery);
			if (response.status && response.statusCode === 200 && response.data.length >= 1) {
				setTotalPages(response?.totalPages);
            	setTotalElements(response?.totalElements);
				const data = response.data.map((obj: any) => ({
					id: obj.media_batch_identifier,
					mediaName: obj.media_name,
					totalPlates: obj.total_plates_produced,
					stages: {
						removal: obj.removal,
						cleaning: obj.cleaning,
						inspection: obj.inspection,
						sterilization: obj.sterilization,
						inventory: obj.sterilization,
					},
					rejectedCount: obj.rejected_count,
					lastUpdated: obj.last_updated,
				}));

				setPostAutoClaveBatches(data);
			} else {
				setPostAutoClaveBatches([]);
				 setTotalPages(0)
				setTotalElements(0)
			}
		} catch (error) {
			console.error('user response', error);
		}
	}

	const handleFromDateChange = (e : string) => {
		setFromDate(e)
		 // Reset toDate if it's before the new fromDate
		if (toDate && new Date(e) > new Date(toDate)) {
			setToDate("");
		}
	}

	// render all inital apis
	useEffect(() => {
		getPostAutoclaveBatchList(page, pageSize, mediaFilter, searchQuery);

		if (useEffectRequired) {
			setUseEffectRequired(false);
		}
	}, [useEffectRequired, mediaFilter, searchQuery]);

	useEffect(() => {
		getPostAutoClaveBatchStat();
		getPlateTypeList()
	}, []);



	const selectedBatch = useMemo(() => {
		return postAutoClaveBatches?.find(b => b.id === selectedBatchId);
	}, [selectedBatchId, postAutoClaveBatches]);

	return (
		<div className="flex flex-col h-full bg-[#F8FAFC] media-prep-scope">
			<HeaderSection />
			<PageHeader pageHeader={t('postAutoclaveProcess.postAutoclaveStatus.postAutoclaveBatchStatus')} pageDescription={t('pageDescription.postAutoclaveBatchStatus.description')} />
			{/* Header */}
			<div className="bg-white border-b border-slate-200 shadow-sm px-4 sm:px-6 lg:px-8 py-5">
				<div className="flex flex-col xl:flex-row xl:items-end gap-4">

					{/* Search */}
					<div className="w-full xl:flex-1">
						<div className="relative">
							<Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

							<input
								type="text"
								placeholder="Search Batch ID or Media..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="w-full h-11 pl-11 pr-4 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
							/>

							<CircleX onClick={() => setSearchQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
						</div>
					</div>

					{/* Filters */}
					<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-3 w-full xl:w-auto">
						<select
							value={mediaFilter}
							onChange={(e) => setMediaFilter(e.target.value)}
							className="h-11 min-w-[180px] rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-200"
						>
							<option value="">All Media Types</option>

							{mediaTypes?.map((mediaType: any) => (
								<option key={mediaType.id} value={mediaType.id}>
									{mediaType.plate_type}
								</option>
							))}
						</select>
{/* 
						<select
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							className="h-11 min-w-[180px] rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-200"
						>
							<option value="">All Statuses</option>
							<option value="PENDING">Pending</option>
							<option value="IN PROGRESS">In Progress</option>
							<option value="COMPLETED">Completed</option>
						</select> */}
					</div>

					{/* Date Filters */}
					{/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full xl:w-auto">
						<div className="flex flex-col">
							<label className="mb-1 text-sm font-medium text-slate-600">
								From Date
							</label>

							<input
								type="date"
								value={fromDate}
								onChange={(e) => handleFromDateChange(e.target.value)}
								className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
							/>
						</div>

						<div className="flex flex-col">
							<label className="mb-1 text-sm font-medium text-slate-600">
								To Date
							</label>

							<input
								type="date"
								value={toDate}
								min={fromDate}
								onChange={(e) => setToDate(e.target.value)}
								className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
							/>
						</div>
					</div> */}

				</div>
			</div>

			<div className="p-8 flex-1 overflow-y-auto custom-scrollbar space-y-8">
				{/* Statistics Overview */}
				<div className="grid grid-cols-4 lg:grid-cols-8 gap-4">
					{[
						{ label: 'Total Batches', value: postAutoClaveBatchesStat?.totalBatches, icon: 'layers', lightColor: 'bg-blue-50', textColor: 'text-blue-600' },
						{ label: 'Total Plates', value: postAutoClaveBatchesStat?.totalPlates, icon: 'inventory_2', lightColor: 'bg-indigo-50', textColor: 'text-indigo-600' },
						{ label: 'Pending Cleaning', value: postAutoClaveBatchesStat?.pendingCleaning, icon: 'cleaning_services', lightColor: 'bg-amber-50', textColor: 'text-amber-600' },
						{ label: 'Pending Inspection', value: postAutoClaveBatchesStat?.pendingInspection, icon: 'fact_check', lightColor: 'bg-amber-50', textColor: 'text-amber-600' },
						{ label: 'Pending Sterilization', value: postAutoClaveBatchesStat?.pendingSterilization, icon: 'sanitizer', lightColor: 'bg-orange-50', textColor: 'text-orange-600' },
						{ label: 'Pending Inventory', value: postAutoClaveBatchesStat?.pendingInventory, icon: 'inventory', lightColor: 'bg-amber-50', textColor: 'text-amber-600' },
						{ label: 'Completed', value: postAutoClaveBatchesStat?.completedBatches, icon: 'check_circle', lightColor: 'bg-emerald-50', textColor: 'text-emerald-600' },
						{ label: 'Rejected Plates', value: postAutoClaveBatchesStat?.totalRejected, icon: 'cancel', lightColor: 'bg-red-50', textColor: 'text-red-600' }
					].map((stat, idx) => (
						<button key={idx} className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all text-left group">
							<div className={`w-8 h-8 ${stat.lightColor} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
								<span className={`material-symbols-outlined ${stat.textColor} text-lg`}>{stat.icon}</span>
							</div>
							<p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-tight">{stat.label}</p>
							<h3 className="text-xl font-black text-[#1E293B]">{stat.value}</h3>
						</button>
					))}
				</div>

				{/* Batch Listing Table */}
				<div className="bg-white p-3 rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
					<div className="px-8 py-2 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
						<div className="flex items-center gap-3">
							<span className="material-symbols-outlined text-orange-500 text-xl">list_alt</span>
							<h3 className="text-xs pt-2 font-black text-[#1E293B] uppercase tracking-[0.15em]">Active Post-Autoclave Batches</h3>
						</div>
						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-100 rounded-full">
								<span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
								<span className="text-[10px] font-black text-red-600 uppercase tracking-widest">{postAutoClaveBatchesStat?.totalRejected} Rejected Plates Detected</span>
							</div>
						</div>
					</div>

					<div className="overflow-scroll h-[50vh]">
						<table className="w-full text-left border-collapse">
							<thead className="sticky top-0 z-10 bg-white border-b border-slate-100">
								<tr>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Batch ID</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Media Name</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Plates</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Removal</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Cleaning</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Inspection</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Sterilization</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Inventory</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Rejected</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Last Updated</th>
									<th className="px-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">View</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-50">
								{postAutoClaveBatches?.map((batch) => {
									const rejected = Number(batch?.rejectedCount ?? 0);
									return (
										<tr key={batch.id} className={`hover:bg-slate-50 transition-colors group ${rejected > 0 ? 'bg-red-50/10' : ''}`}>
											<td className="px-1 py-2">
												<span className="text-sm font-black text-[#1E293B]">{batch.id}</span>
											</td>
											<td className="px-1 py-2">
												<span className="text-xs font-bold text-slate-700">{batch.mediaName}</span>
											</td>
											<td className="px-1 py-2">
												<div className="flex flex-col gap-2">
													<span className="text-xs font-black text-slate-900">{batch.totalPlates}</span>
													<div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
														<div
															className="h-full bg-orange-500 rounded-full transition-all duration-500"
															style={{
																width: `${((batch.stages.removal.status === 'COMPLETED' ? 1 : 0) +
																	(batch.stages.cleaning.status === 'COMPLETED' ? 1 : 0) +
																	(batch.stages.inspection.status === 'COMPLETED' ? 1 : 0) +
																	(batch.stages.sterilization.status === 'COMPLETED' ? 1 : 0) +
																	(batch.stages.inventory.status === 'COMPLETED' ? 1 : 0)) / 5 * 100
																	}%`
															}}
														></div>
													</div>
												</div>
											</td>
											<td className="px-1 py-2 text-center">
												<span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${getStatusColor(batch.stages.removal.status)}`}>
													{batch.stages.removal.status}
												</span>
											</td>
											<td className="px-1 py-2 text-center">
												<span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${getStatusColor(batch.stages.cleaning.status)}`}>
													{batch.stages.cleaning.status}
												</span>
											</td>
											<td className="px-1 py-2 text-center">
												<span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${getStatusColor(batch.stages.inspection.status)}`}>
													{batch.stages.inspection.status}
												</span>
											</td>
											<td className="px-1 py-2 text-center">
												<span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${getStatusColor(batch.stages.sterilization.status)}`}>
													{batch.stages.sterilization.status}
												</span>
											</td>
											<td className="px-1 py-2 text-center">
												<span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${getStatusColor(batch.stages.inventory.status)}`}>
													{batch.stages.inventory.status}
												</span>
											</td>
											<td className="px-1 py-2">
												<span className={`text-xs font-black ${rejected > 0 ? 'text-red-600' : 'text-slate-400'}`}>
													{rejected}
												</span>
											</td>
											<td className="px-1 py-2">
												<span className="text-[10px] font-bold text-slate-400">{helperFunctions.formatDate(batch.lastUpdated, true)}</span>
											</td>
											<td className="px-1 py-2 text-right">
												<button
													onClick={() => setSelectedBatchId(batch.id)}
													className="w-10 h-10 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-slate-400 hover:text-orange-500 hover:border-orange-100 hover:shadow-lg transition-all"
												>
													<span className="material-symbols-outlined text-xl">visibility</span>
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					<div>
						<Pagination
							page={page}
							totalPages={totalPages}
							handlePageChange={handlePageChange}
						/>
					</div>

					{/* <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
						<p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Showing {filteredBatches.length} of {stats.totalBatches} batches</p>
						<div className="flex gap-2">
							<button className="p-2 text-slate-400 hover:bg-white rounded-xl border border-transparent hover:border-slate-200 transition-all disabled:opacity-30" disabled>
								<span className="material-symbols-outlined">chevron_left</span>
							</button>
							<button className="p-2 text-slate-400 hover:bg-white rounded-xl border border-transparent hover:border-slate-200 transition-all disabled:opacity-30" disabled>
								<span className="material-symbols-outlined">chevron_right</span>
							</button>
						</div>
					</div> */}
				</div>
			</div>

			{/* Batch Lifecycle Viewer Modal */}
			{selectedBatch && (
				<div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 animate-in fade-in duration-300">
					<div className="bg-white rounded-[40px] shadow-2xl w-full max-w-[960px] overflow-hidden animate-in zoom-in duration-300 flex flex-col max-h-[90vh]">
						<div className="px-10 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/30 shrink-0">
							<div>
								<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Batch Lifecycle Viewer</p>
								<h3 className="text-xl font-black text-[#1E293B] tracking-tight">Batch ID: {selectedBatch.id}</h3>
							</div>
							<button
								onClick={() => setSelectedBatchId(null)}
								className="w-8 h-8 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-100 transition-all shadow-sm"
							>
								<span className="material-symbols-outlined text-xl">close</span>
							</button>
						</div>

						<div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
							<div className="space-y-6 relative">
								{/* Vertical Timeline Line */}
								<div className="absolute left-6 top-2 bottom-2 w-0.5 bg-slate-100"></div>

								{[
									{ stage: 'Removal from Autoclave', key: 'removal', icon: LogOut },
									{ stage: 'Plate Cleaning Assessment', key: 'cleaning', icon: BrushCleaning },
									{ stage: 'Plate Physical Inspection', key: 'inspection', icon: Microscope },
									{ stage: 'Empty Plate Sterilization', key: 'sterilization', icon: Bubbles },
									{ stage: 'Return to Inventory', key: 'inventory', icon: ShelvingUnit }
								].map((step, idx) => {
									const detail = selectedBatch.stages[step.key as keyof typeof selectedBatch.stages];
									const isCompleted = detail.status === 'COMPLETED';
									const isPending = detail.status === 'PENDING';
									const isInProgress = detail.status === 'IN PROGRESS';

									return (
										<div key={idx} className="relative pl-16 group">
											{/* Timeline Dot */}
											<div className={`absolute left-[18px] top-1 w-3 h-3 rounded-full border-2 z-10 transition-all duration-500 ${isCompleted ? 'bg-emerald-500 border-emerald-500' :
												isInProgress ? 'bg-blue-500 border-blue-500 animate-pulse' : 'bg-white border-slate-300'
												}`}></div>

											<div className={`p-4 rounded-2xl border transition-all duration-300 ${isCompleted ? 'bg-emerald-50/30 border-emerald-100' :
												isInProgress ? 'bg-blue-50/30 border-blue-100 shadow-sm' : 'bg-white border-slate-100'
												}`}>
												<div className="flex justify-between items-start">
													<div className="flex items-center gap-4">
														<div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm ${isCompleted ? 'bg-emerald-500 text-white' :
															isInProgress ? 'bg-blue-500 text-white' : 'bg-slate-50 text-slate-400'
															}`}>
															<step.icon className='material-symbols-outlined text-xl' />
														</div>
														<div>
															<h4 className="text-sm font-black text-[#1E293B]">{step.stage}</h4>
															<div className="flex items-center gap-3 mt-1">
																<span className={`text-[9px] font-black uppercase tracking-widest ${isCompleted ? 'text-emerald-600' :
																	isInProgress ? 'text-blue-600' : 'text-amber-600'
																	}`}>
																	{detail.status}
																</span>
																{detail.completed_at && (
																	<span className="text-[9px] font-bold text-slate-400 flex items-center gap-1">
																		<span className="material-symbols-outlined text-[10px]">calendar_today</span>
																		{helperFunctions.formatDate(detail.completed_at, true)}
																	</span>
																)}
															</div>
														</div>
													</div>

													{isPending && (
														<button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-[9px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 hover:border-orange-200 hover:text-orange-500 transition-all flex items-center gap-2">
															Start Activity
															<span className="material-symbols-outlined text-sm">arrow_forward</span>
														</button>
													)}
													{isInProgress && (
														<button className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2">
															Continue
															<span className="material-symbols-outlined text-sm">arrow_forward</span>
														</button>
													)}
												</div>

												{(isCompleted || isInProgress) && (
													<div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-slate-100/50">
														<div>
															<p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Operator</p>
															<p className="text-xs font-bold text-slate-700">{detail.operator_name || '—'}</p>
														</div>
														<div>
															<p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Plate Count</p>
															<p className="text-xs font-bold text-slate-700">{detail.eligible || '—'}</p>
														</div>
														<div>
															<p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Rejected</p>
															<p className={`text-xs font-black ${detail.rejected ? 'text-red-500' : 'text-slate-400'}`}>
																{detail.rejected || 0}
															</p>
														</div>
													</div>
												)}

												{/* {detail.rejectedPlates && detail.rejectedPlates > 0 && (
													<div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3">
														<span className="material-symbols-outlined text-red-500 text-lg">warning</span>
														<p className="text-[10px] font-bold text-red-700">
															{detail.rejectedPlates} plates rejected due to: <span className="font-black">{detail.rejectionReason}</span>
														</p>
													</div>
												)} */}
											</div>
										</div>
									);
								})}
							</div>
						</div>

						<div className="px-10 py-4 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
							<button
								onClick={() => setSelectedBatchId(null)}
								className="px-10 py-3 bg-[#2D3E50] text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all"
							>
								Close Lifecycle View
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PostAutoclaveProcess;
